import { Resolver } from "./types"

const GUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i

export const isLikelyGuid = (s: string): boolean => GUID_RE.test(s)

export const knownSegments = new Set<string>([
  "applications",
  "roles",
  "permissions",
  "approvers",
  "properties",
  "values",
  "firmware-constraints",
  "keyValuePairs",
  "devices",
  "categories",
  "families",
  "software-package-firmware-constraints",
  "applicationRoles",
  "notifications",
  "topics",
  "feedbacks",
  "issue-reports",
  "role-requests",
  "users",
  "apps-roles",
  "apps-permissions",
  "files",
  "versions",
  "app-registrations",
  "deprecated-devices",
  "factory-reports",
  "factories",
  "production",
  "key-values",
  "supplierPortal",
  "manufacturedDevices",
  "objectIdsRequests",
  "objectIds",
  "gateways",
  "trainings",
  "courses",
  "settings",
  "create",
])

const prop = (obj: unknown, key: string): unknown =>
  obj && typeof obj === "object" ? (obj as Record<string, unknown>)[key] : undefined

const asString = (v: unknown): string => (typeof v === "string" ? v : "")

function getTopicCtx(parents: Set<string>): string {
  if (parents.has("notifications")) {
    return "notif"
  }
  if (parents.has("feedbacks")) {
    return "fb"
  }
  return "ir"
}

export const resolverMap: Record<string, Resolver> = {
  applications: {
    endpoint: ({ currentId }) => `/applications/${currentId}`,
    queryKey: ({ currentId }) => ["breadcrumb", "application", currentId] as const,
    select: (d) => asString(prop(d, "name")),
  },
  roles: {
    endpoint: ({ ids, currentId }) => {
      if (ids.users && ids.applications) {
        return `/users/${ids.users}/applications/${ids.applications}/roles/${currentId}`
      }
      if (ids.applications) {
        return `/applications/${ids.applications}/roles/${currentId}`
      }
      if (ids.devices) {
        return `/devices/${ids.devices}/roles/${currentId}`
      }
      return ""
    },
    queryKey: ({ ids, currentId }) =>
      [
        "breadcrumb",
        "role",
        ids.users ?? "",
        ids.applications ?? ids.devices ?? "",
        currentId,
      ] as const,
    select: (d) => {
      const role = prop(d, "role")
      if (role) {
        return asString(prop(role, "name"))
      }
      return asString(prop(d, "name"))
    },
  },
  permissions: {
    endpoint: ({ ids, currentId }) => {
      if (ids.users && ids.applications) {
        return `/users/${ids.users}/applications/${ids.applications}/permissions/${currentId}`
      }
      if (ids.applications && ids.roles) {
        return `/applications/${ids.applications}/roles/${ids.roles}/permissions/${currentId}`
      }
      if (ids.devices && ids.roles) {
        return `/devices/${ids.devices}/roles/${ids.roles}/permissions/${currentId}`
      }
      if (ids.applications) {
        return `/applications/${ids.applications}/permissions/${currentId}`
      }
      return ""
    },
    queryKey: ({ ids, currentId }) =>
      [
        "breadcrumb",
        "permission",
        ids.users ?? "",
        ids.applications ?? ids.devices ?? "",
        ids.roles ?? "",
        currentId,
      ] as const,
    select: (d) => {
      const perm = prop(d, "permission")
      if (perm) {
        return asString(prop(perm, "name"))
      }
      return asString(prop(d, "name"))
    },
  },
  approvers: {
    endpoint: ({ ids, currentId }) => {
      if (ids.applications && ids.roles) {
        return `/applications/${ids.applications}/roles/${ids.roles}/approvers/${currentId}`
      }
      return ""
    },
    queryKey: ({ ids, currentId }) =>
      [
        "breadcrumb",
        "approver",
        ids.applications ?? "",
        ids.roles ?? "",
        currentId,
      ] as const,
    select: (d) => {
      const user = prop(d, "user")
      if (!user) {
        return ""
      }
      const first = asString(prop(user, "first"))
      const last = asString(prop(user, "last"))
      const full = `${first} ${last}`.trim()
      return full || asString(prop(user, "username"))
    },
  },
  "firmware-constraints": {
    endpoint: ({ ids, currentId }) => {
      if (ids.applications && ids.permissions) {
        return `/applications/${ids.applications}/permissions/${ids.permissions}/firmwareConstraints/${currentId}`
      }
      return ""
    },
    queryKey: ({ ids, currentId }) =>
      [
        "breadcrumb",
        "fwConstraint",
        ids.applications ?? "",
        ids.permissions ?? "",
        currentId,
      ] as const,
    select: (d) => {
      const device = prop(d, "device")
      return device ? asString(prop(device, "name")) : ""
    },
  },
  keyValuePairs: {
    endpoint: ({ ids, currentId }) => {
      if (ids.applications) {
        return `/applications/${ids.applications}/keyValuePairs/${currentId}`
      }
      return ""
    },
    queryKey: ({ ids, currentId }) =>
      ["breadcrumb", "appKvp", ids.applications ?? "", currentId] as const,
    select: (d) => asString(prop(d, "key")),
  },
  devices: {
    endpoint: ({ currentId }) => `/devices/${currentId}`,
    queryKey: ({ currentId }) => ["breadcrumb", "device", currentId] as const,
    select: (d) => asString(prop(d, "name")),
  },
  families: {
    endpoint: ({ currentId }) => `/deviceFamilies/${currentId}`,
    queryKey: ({ currentId }) => ["breadcrumb", "family", currentId] as const,
    select: (d) => asString(prop(d, "name")),
  },
  categories: {
    endpoint: ({ currentId }) => `/deviceCategories/${currentId}`,
    queryKey: ({ currentId }) => ["breadcrumb", "category", currentId] as const,
    select: (d) => asString(prop(d, "name")),
  },
  "software-package-firmware-constraints": {
    endpoint: ({ ids, currentId }) => {
      if (ids.devices) {
        return `/devices/${ids.devices}/softwarePackageFirmwareConstraints/${currentId}`
      }
      return ""
    },
    queryKey: ({ ids, currentId }) =>
      ["breadcrumb", "spfwConstraint", ids.devices ?? "", currentId] as const,
    select: (d) => asString(prop(d, "softwarePackageEditionCode")),
  },
  notifications: {
    endpoint: ({ currentId }) => `/notifications/${currentId}`,
    queryKey: ({ currentId }) => ["breadcrumb", "notification", currentId] as const,
    select: (d) => asString(prop(d, "title")),
  },
  topics: {
    endpoint: ({ parents, currentId }) => {
      if (parents.has("notifications")) {
        return `/notificationTopics/${currentId}`
      }
      if (parents.has("feedbacks")) {
        return `/feedbackTopics/${currentId}`
      }
      if (parents.has("issue-reports")) {
        return `/issueReportTopics/${currentId}`
      }
      return ""
    },
    queryKey: ({ parents, currentId }) => {
      const ctx = getTopicCtx(parents)
      return ["breadcrumb", "topic", ctx, currentId] as const
    },
    select: (d) => asString(prop(d, "name")),
  },
  feedbacks: {
    endpoint: ({ currentId }) => `/feedbacks/${currentId}`,
    queryKey: ({ currentId }) => ["breadcrumb", "feedback", currentId] as const,
    select: (d) => {
      const topic = prop(d, "topic")
      return topic ? asString(prop(topic, "name")) : ""
    },
  },
  "issue-reports": {
    endpoint: ({ currentId }) => `/issueReports/${currentId}`,
    queryKey: ({ currentId }) => ["breadcrumb", "issueReport", currentId] as const,
    select: (d) => asString(prop(d, "subject")),
  },
  "role-requests": {
    endpoint: ({ currentId }) => `/roleRequests/${currentId}`,
    queryKey: ({ currentId }) => ["breadcrumb", "roleRequest", currentId] as const,
    select: (d) => {
      const app = prop(d, "application")
      const role = prop(d, "role")
      const appName = app ? asString(prop(app, "name")) : ""
      const roleName = role ? asString(prop(role, "name")) : ""
      if (appName && roleName) {
        return `${appName} / ${roleName}`
      }
      return appName || roleName
    },
  },
  users: {
    endpoint: ({ currentId }) => `/users/${currentId}`,
    queryKey: ({ currentId }) => ["breadcrumb", "user", currentId] as const,
    select: (d) => {
      const first = asString(prop(d, "first"))
      const last = asString(prop(d, "last"))
      const full = `${first} ${last}`.trim()
      return full || asString(prop(d, "username"))
    },
  },
  files: {
    endpoint: ({ currentId }) => `/files/${currentId}`,
    queryKey: ({ currentId }) => ["breadcrumb", "file", currentId] as const,
    select: (d) => asString(prop(d, "name")),
  },
  versions: {
    endpoint: ({ ids, currentId }) => {
      if (ids.files) {
        return `/files/${ids.files}/versions/${currentId}`
      }
      return ""
    },
    queryKey: ({ ids, currentId }) =>
      ["breadcrumb", "fileVersion", ids.files ?? "", currentId] as const,
    select: (d) => asString(prop(d, "suffix")),
  },
  "app-registrations": {
    endpoint: ({ currentId }) => `/appRegistrations/${currentId}`,
    queryKey: ({ currentId }) => ["breadcrumb", "appReg", currentId] as const,
    select: (d) => asString(prop(d, "name")),
  },
  "deprecated-devices": {
    endpoint: ({ currentId }) => `/deprecatedDevices/${currentId}`,
    queryKey: ({ currentId }) =>
      ["breadcrumb", "deprecatedDevice", currentId] as const,
    select: (d) => asString(prop(d, "deviceSerialNumber")),
  },
  factories: {
    endpoint: ({ currentId }) => `/factories/${currentId}`,
    queryKey: ({ currentId }) => ["breadcrumb", "factory", currentId] as const,
    select: (d) => asString(prop(d, "name")),
  },
  "key-values": {
    endpoint: ({ parents, currentId }) => {
      if (parents.has("production")) {
        return `/production/keyValuePairs/${currentId}`
      }
      return ""
    },
    queryKey: ({ currentId }) => ["breadcrumb", "prodKvp", currentId] as const,
    select: (d) => asString(prop(d, "key")),
  },
  manufacturedDevices: {
    endpoint: ({ parents, currentId }) => {
      if (parents.has("supplierPortal")) {
        return `/litePanelPro/manufacturedDevices/${currentId}`
      }
      if (parents.has("gateways")) {
        return `/gateway/manufacturedDevices/${currentId}`
      }
      return ""
    },
    queryKey: ({ parents, currentId }) => {
      const ctx = parents.has("supplierPortal") ? "sp" : "gw"
      return ["breadcrumb", "manufDevice", ctx, currentId] as const
    },
    select: (d) => {
      const sn = asString(prop(d, "serialNumber"))
      return sn || asString(prop(d, "serialNumberAbb"))
    },
  },
  trainings: {
    endpoint: ({ currentId }) => `/trainings/${currentId}`,
    queryKey: ({ currentId }) => ["breadcrumb", "training", currentId] as const,
    select: (d) => asString(prop(d, "title")),
  },
  courses: {
    endpoint: ({ ids, currentId }) => {
      if (ids.trainings) {
        return `/trainings/${ids.trainings}/courses/${currentId}`
      }
      return ""
    },
    queryKey: ({ ids, currentId }) =>
      ["breadcrumb", "trainingCourse", ids.trainings ?? "", currentId] as const,
    select: (d) => asString(prop(d, "title")),
  },
}

export function getResolver(name: string): Resolver | undefined {
  return resolverMap[name]
}
